import React, { useState } from "react";
import { Table as ReactstrapTable } from "reactstrap";
import { compose, withStateHandlers } from "recompose";
import { withTable, } from "components/common";
import DatePicker from 'react-datepicker';
import { withXeroErrors, withDocumentRenderer } from "components";
import { Trans, withTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { formatCurrency, formatDate } from "services/util";
import { BulkDraftActions } from "../components/BulkDraftActions";
import { BillCreditNoteDocument } from "components/documents";
import FormGroup from "../Settings/EmailTemplates/FormGroup";
import { NotificationManager } from "react-notifications";
import { useOrganisationContext } from "hooks";

const EmailCell = withDocumentRenderer({
  onDownload: () => (getDocument, item) =>
    getDocument(
      {
        item,
      },
      `r_${item.RepeatingInvoiceID}.pdf`
    ),
  type: "SALES_INVOICE",
})(({ EmailDocument, DocumentRenderer, i, selectedOrganisation }) => (
  <>
    <EmailDocument compact item={i} type="SALES_INVOICE" />
    <DocumentRenderer component={BillCreditNoteDocument} selectedOrganisation={selectedOrganisation}/>
  </>
));

const DownloadCell = withDocumentRenderer({
  onDownload: () => (getDocument, item) =>
    getDocument(
      {
        item,
      },
      `r_${item.RepeatingInvoiceID}.pdf`
    ),
  type: "SALES_INVOICE",
})(({ DownloadDocument, DocumentRenderer, i, selectedOrganisation }) => (
  <>
    <DownloadDocument compact item={i} className="mr-1" />
    <DocumentRenderer component={BillCreditNoteDocument} selectedOrganisation={selectedOrganisation}/>
  </>
));

const getRepeatMethod = (unit, period) => {
  if (period === 1) {
    return unit === "MONTHLY" ? "Month" : (unit === "WEEKLY" ? "Week" : unit);
  } else {
    return unit === "MONTHLY" ? `${period} Months` : (unit === "WEEKLY" ? `${period} Weeks` : unit);
  }
};


export const RepeatingTable = ({
  XeroErrors,
  Pagination,
  TableContainer,
  ViewingRecords,
  Loader,
  XeroTableDataFetch,
  data,
  setFilters,
  status,
  payable,
  baseUrl,
  t,
  filters,
  refreshData,
  selected,
}) => {

  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const {selectedOrganisation} = useOrganisationContext()

  const handleInputChange = (event) => {
    setSearch(event.target.value);
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };



  const filteredResults = data.results.filter((item) => {
    const nameCondition = search ? item.Contact.Name.toLowerCase().includes(search.toLowerCase()) : true;
    const referenceCondition = search ? item.Reference.toLowerCase().includes(search.toLowerCase()) : true;
  
    const startDateCondition = startDate ? new Date(item.Schedule.NextScheduledDateString) >= startDate : true;
  
    const endDateCondition = endDate ? new Date(item.Schedule.NextScheduledDateString) <= endDate : true;
  
    if (startDate && endDate) {
      if (startDate > endDate) {
        NotificationManager.error("Start date cannot be greater than end date", "Error");
        return false;
      }
    }
  
    return (nameCondition || referenceCondition) && startDateCondition && endDateCondition;
  });
  
  
  const accPayCheck = /purchases|payable|accounts_payable/
  const currentUrl = window.location.href;
  const isPayable = accPayCheck.test(currentUrl);

  const view_link = isPayable ? 'purchases' :  'accounts_receivable' 

  return (
    <div className="inv-table">
      <XeroErrors color="warning" />
      <div>
      <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', padding: '5px' }}>
        <FormGroup label="Search" style={{ flex: 2 }}>
          <input
            className="form-control mb-2"
            placeholder={t("Search by name or reference")}
            type="text"
            id="searchInput"
            value={search}
            onChange={handleInputChange}
          />
        </FormGroup>

        <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', flex: 1 }}>
          <FormGroup label="Start Date" style={{ flex: 1 }}>
            <DatePicker
              className="form-control"
              selected={startDate}
              placeholderText={t("Select a Date")}
              onChange={handleStartDateChange}
              dateFormat="YYYY-MM-DD"
            />
          </FormGroup>

          <FormGroup label="End Date" style={{ flex: 1 }}>
            <DatePicker
              className="form-control"
              selected={endDate}
              placeholderText={t("Select a Date")}
              onChange={handleEndDateChange}
              dateFormat="YYYY-MM-DD"
            />
          </FormGroup>
        </div>
      </div>
      </div>
      {status === "DRAFT" && (
        <BulkDraftActions
          onSuccess={refreshData}
          items={data.results}
          url="repeatinginvoices"
          pk="RepeatingInvoiceID"
          selected={selected}
        />
      )}
      <div className="small">
        <TableContainer idKey="RepeatingInvoiceID">
          <ReactstrapTable size="sm" className="small" responsive>
            <thead>
              <tr>
                <th select />
                <th className="text-left" sortBy="Contact.Name">
                  <Trans>{payable ? "From" : "To"}</Trans>
                </th>
                <th sortBy="Reference">
                  <Trans>Ref</Trans>
                </th>                
                <th sortBy="Repeats">
                  <Trans>Repeats</Trans>
                </th>
                <th sortBy="Schedule.NextScheduledDate">
                  <Trans>Next Bill Date</Trans>
                </th>
                <th className="text-nowrap" sortBy="Status">
                  <Trans>Status</Trans>
                </th>
                <th className="text-nowrap" sortBy="Total">
                  <Trans>Due</Trans>
                </th>               
                <th className="text-nowrap">
                  <Trans>Actions</Trans>
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.loading && (
                <tr>
                  <td colSpan="9" className="text-center">
                    <span className="fa fa-spinner fa-spin mt-2" /> <Trans>Loading...</Trans>
                  </td>
                </tr>
              )}
              {filteredResults.map((i, idx) => (
                <tr key={i.RepeatingInvoiceID}>
                  <td select />
                  <td className="text-left">
                    {i.Contact ? (
                      <Link to={`/${view_link}/repeating/view/${i.RepeatingInvoiceID}`}>
                        {i.Contact.Name}
                      </Link>
                    ) : null}
                  </td>
                  <td>
                    <Link to={`/${view_link}/repeating/view/${i.RepeatingInvoiceID}`}>
                      {i.Reference || (
                        <Trans>No reference</Trans>
                      )}
                    </Link>
                  </td>  
                  <td>Every {getRepeatMethod(i.Schedule.Unit, i.Schedule.Period)}</td>      
                  <td>{formatDate(i.Schedule.NextScheduledDate)}</td>
                  <td>{i.Status}</td> 
                  <td>{formatCurrency(i.Total)}</td>                            
                  <td>
                    <DownloadCell i={i} selectedOrganisation={selectedOrganisation}/>
                    <EmailCell i={i} selectedOrganisation={selectedOrganisation}/>
                  </td>
                </tr>
              ))}
            </tbody>
          </ReactstrapTable>
        </TableContainer>
      </div>
      <XeroTableDataFetch />
      {/* <div className="d-flex justify-content-between">
        <Pagination size="sm" count={0} noPages />
        <ViewingRecords noCount />
      </div> */}
       <div className="float-right">
        <Pagination size="sm" noPages />
      </div>
      <div className="clearfix">
        <ViewingRecords noCount className="float-left" />
      </div>
    </div>
  );
};

export const enhance = compose(
  withTranslation(),
  withStateHandlers(({ payable }) => {
    return {
      baseUrl: payable
        ? "/accounts_payable/repeatinginvoices"
        : "/accounts_receivable/repeatinginvoices",
    };
  }),
  withXeroErrors(),
  withTable({
    limit: 100,
    url: "repeatinginvoices",
    ordering: {
      column: "Schedule.NextScheduledDate",
      direction: 2,
    },
  })
);

export default enhance(RepeatingTable);
